import { useState } from 'react';
import { toast } from '@/lib/toast';
import { fetchProductTagsByIds, type ProductFieldUpdate } from '@/lib/api';
import { useBulkUpdateProductFields } from '@/hooks/useProducts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FilterChips } from '@/components/ui/filter-chips';
import type { BulkTargets } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targets: BulkTargets;
  onApplied: () => void;
}

type Field = 'type' | 'vendor' | 'tags';
type TagMode = 'add' | 'remove' | 'replace';

const FIELD_CHIPS = [
  { value: 'type', label: 'Product type' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'tags', label: 'Tags' },
];

const TAG_MODE_CHIPS = [
  { value: 'add', label: 'Add tags' },
  { value: 'remove', label: 'Remove tags' },
  { value: 'replace', label: 'Replace all tags' },
];

/** Parse a comma-separated tag input into a clean, de-duplicated list. */
function parseTags(input: string): string[] {
  return [...new Set(input.split(',').map((t) => t.trim()).filter(Boolean))];
}

/**
 * Bulk-edit product-level fields — product type, vendor, and tags — for every
 * selected product. Tag edits merge against each product's existing tags so
 * "add" / "remove" never clobber tags they did not touch.
 */
export function BulkProductFieldsDialog({ open, onOpenChange, targets, onApplied }: Props) {
  const [field, setField] = useState<Field>('type');
  const [tagMode, setTagMode] = useState<TagMode>('add');
  const [value, setValue] = useState('');
  const bulkUpdate = useBulkUpdateProductFields();
  const pending = bulkUpdate.isPending;

  const productIds = targets.productShopifyIds;

  const reset = () => {
    setValue('');
    setField('type');
    setTagMode('add');
  };

  const handleApply = async () => {
    if (productIds.length === 0) return;
    try {
      let updates: ProductFieldUpdate[];

      if (field === 'type' || field === 'vendor') {
        const trimmed = value.trim();
        const column = field === 'type' ? 'product_type' : 'vendor';
        updates = productIds.map((id) => ({
          shopifyProductId: id,
          fields: { [column]: trimmed },
        }));
      } else {
        const tags = parseTags(value);
        if (tags.length === 0) {
          toast.error('Enter at least one tag');
          return;
        }
        if (tagMode === 'replace') {
          updates = productIds.map((id) => ({
            shopifyProductId: id,
            fields: { tags },
          }));
        } else {
          // Add / remove must merge against each product's current tags.
          const current = await fetchProductTagsByIds(productIds);
          const tagSet = new Set(tags);
          updates = current.map((p) => {
            const existing = p.tags ?? [];
            const next =
              tagMode === 'add'
                ? [...new Set([...existing, ...tags])]
                : existing.filter((t) => !tagSet.has(t));
            return { shopifyProductId: p.shopify_product_id, fields: { tags: next } };
          });
        }
      }

      await bulkUpdate.mutateAsync(updates);
      toast.success(`Updated ${updates.length} products in Shopify`);
      reset();
      onOpenChange(false);
      onApplied();
    } catch (err) {
      toast.error(`Bulk update failed: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit product details</DialogTitle>
          <DialogDescription>
            Applies to {targets.selectionLabel} ({productIds.length}{' '}
            {productIds.length === 1 ? 'product' : 'products'}).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="mb-2 block text-xs font-medium text-muted-foreground">
              Field to edit
            </label>
            <FilterChips
              options={FIELD_CHIPS}
              value={field}
              onChange={(v) => setField(v as Field)}
            />
          </div>

          {field === 'tags' && (
            <div>
              <label className="mb-2 block text-xs font-medium text-muted-foreground">
                How to change tags
              </label>
              <FilterChips
                options={TAG_MODE_CHIPS}
                value={tagMode}
                onChange={(v) => setTagMode(v as TagMode)}
              />
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {field === 'type'
                ? 'Product type'
                : field === 'vendor'
                  ? 'Vendor'
                  : 'Tags (comma-separated)'}
            </label>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={
                field === 'tags' ? 'e.g. perennial, full-sun, zone-5' : 'Enter a value'
              }
              autoFocus
            />
            {(field === 'type' || field === 'vendor') && value.trim() === '' && (
              <p className="mt-1.5 text-[11px] text-warning">
                Leaving this empty will clear the {field} on every selected product.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button className="w-full sm:w-auto" onClick={handleApply} disabled={pending}>
            {pending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
